import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Checkbox, Divider, Form, Message, Segment, Transition } from 'semantic-ui-react'
import { Field, Form as FinalForm } from 'react-final-form'
import Papa from 'papaparse'

import { PRODUCT_TYPE_ID_BY_KEY, BATCH_REQUEST_DELAY } from '../../../consts'
import { getProducts, populateBatchProducts } from '../../../redux/actions/products'
import {
  setBatchLookupValues,
  setBatchLookupTimeouts,
  resetBatchData,
  setBatchLookupStage,
  setEnableDeepMatcher,
} from '../../../redux/actions/ui'
import { AppState } from '../../../redux/store'
import { BatchLookupValue, LookupOptionValues } from '../../../redux/types/ui'
import { updateBatchAliasAssignments } from '../../../redux/actions/productAliases'
import { productAttributesLinter } from '../../../utils/productAttributesLinter'
import { FileParsingError } from '../../../utils/errors'
import { LookupOptionsFormGroup } from '../LookupOptionsFormGroup'
import { saveLocal } from '../../../utils/localStorage'

interface Props {}

declare global {
  interface Window {
    BATCH_REQUEST_DELAY: number
  }
}

interface BatchLookupOptionValues extends LookupOptionValues {
  file: File | null
}

const BatchFileUpload = (props: Props) => {
  const dispatch = useDispatch()
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [totalProgress, setTotalProgress] = useState(-1)
  const [currentProgress, setCurrentProgress] = useState(0)
  const {
    batchProgress,
    batchProgressError,
    batchLookupOptionValues,
    searchFunc,
    enableDeepMatcher,
  } = useSelector((state: AppState) => state.ui)
  const { batchProducts, batchProductPhotos } = useSelector((state: AppState) => state.products)
  const brandSearchMode = useSelector((state: AppState) => state.products.brandSearchMode)
  const { batchAliasesById } = useSelector((state: AppState) => state.productAliases)
  const lookupValues: BatchLookupOptionValues = useMemo(() => {
    return {
      file: null,
      ...batchLookupOptionValues,
    }
  }, [batchLookupOptionValues])

  useEffect(() => {
    const currentProgress = batchProgress.filter((a) => a === 1).length
    setCurrentProgress(currentProgress)

    if (totalProgress === currentProgress) {
      Object.keys(batchProducts).forEach((key) => {
        const product = batchProducts[key]?.[0]
        if (product) {
          const photos = batchProductPhotos[key]?.[product.id] || []
          const aliases = batchAliasesById[key]?.[product.id] || []
          dispatch(
            updateBatchAliasAssignments({
              batchId: key,
              batchSelectedProduct: {
                ...product,
                attributes: productAttributesLinter(product.attributes),
              },
              batchSelectedProductPhotos: photos,
              batchSelectedProductAliases: aliases,
              isConfirmed: false,
            })
          )
        }
      })
      dispatch(populateBatchProducts({ batchId: '0' }))

      setTimeout(() => {
        setIsLookingUp(false)
        dispatch(setBatchLookupStage({ stage: 'review' }))
      }, 500)
    }
  }, [
    dispatch,
    batchProgressError,
    batchProgress,
    totalProgress,
    batchProducts,
    batchProductPhotos,
    batchAliasesById,
    lookupValues,
    searchFunc,
    enableDeepMatcher,
  ])

  const onSubmit = (values: BatchLookupOptionValues) => {
    const lookupValues: BatchLookupValue[] = []
    const { file, ...lookupOptions } = values

    if (!file) {
      toast('Select a CSV file to process', {
        containerId: 'error',
      })
      return
    }

    const parseLines = (lines: Papa.ParseResult<string[]>) => {
      for (const [index, line] of lines.data.entries()) {
        if (!line[1]) {
          throw new FileParsingError(`Name cannot be empty (around line ${index + 1})`)
        }

        const [store, name, alias, original_product_type, pre_selected_id] = [
          line[0]?.trim(), // store name
          line[1]?.trim(), // name
          line[2]?.trim(), // alias
          line[3]?.trim().toLowerCase(), // product-type
          line[4] ? parseInt(line[4].trim()) : null, // gets preselected product id
        ]

        let product_type = original_product_type
        const vapeRegex = new RegExp(
          '.*(cart|vape|pod|dart.pod|dispo|pax|ccell|510|-car|read.to.use|vfire|maxx).*',
          'i'
        )
        const vapenRegex = new RegExp('.*(vapen).*', 'i')

        if (
          original_product_type === 'extract' &&
          vapeRegex.test(alias) &&
          !vapenRegex.test(alias)
        ) {
          product_type = 'vape'
        }

        let value: BatchLookupValue = {
          store,
          name,
          name_mode: 'strict',
          alias,
          productType: PRODUCT_TYPE_ID_BY_KEY[product_type]?.toString(),
          lineage: '',
          brand: '',
          brand_mode: 'phonetic',
          brandSubtype: '',
          brandMode: 'phonetic',
          productSubtype: '',
          amount: '',
          dosage: '',
          no_amount: false,
          no_lineage: false,
          no_brand_subtype: false,
          no_product_subtype: false,
          no_dosage: false,
          preSelectedId: pre_selected_id,
          ...lookupOptions,
        }
        lookupValues.push(value)
      }
    }

    const processLookupValues = () => {
      setIsLookingUp(true)
      const timeouts: number[] = []

      setTotalProgress(lookupValues.length)
      const batchDelay = window.BATCH_REQUEST_DELAY || BATCH_REQUEST_DELAY[searchFunc]
      lookupValues.map((query, index) =>
        timeouts.push(
          // prefixing window here for type consideration
          // [ref] https://stackoverflow.com/questions/51040703/what-return-type-should-be-used-for-settimeout-in-typescript
          window.setTimeout(() => {
            dispatch(
              getProducts({
                values: query,
                searchFunc,
                batchId: index.toString(),
                brandSearchMode,
                isBatchLookupMode: true,
                enableDeepMatcher,
              })
            )
          }, batchDelay * index)
        )
      )
      dispatch(setBatchLookupTimeouts({ timeouts: timeouts }))
      dispatch(setBatchLookupValues({ batchLookupValues: lookupValues }))
    }

    if (file) {
      dispatch(resetBatchData())

      Papa.parse<string[]>(file, {
        skipEmptyLines: 'greedy',
        delimiter: ',',
        complete: function (lines) {
          try {
            parseLines(lines)
            if (lookupValues.length > 0) {
              processLookupValues()
            }
          } catch (e) {
            setIsLookingUp(false)
            if (e instanceof FileParsingError && !toast.isActive('file-parsing-error')) {
              toast(e.message, {
                containerId: 'error',
                toastId: 'file-parsing-error',
              })
            }
          }
        },
      })
    }
  }

  return (
    <Segment padded="very">
      <h3>Upload a CSV file to get started</h3>
      <FinalForm
        onSubmit={onSubmit}
        initialValues={lookupValues}
        render={({ handleSubmit, values, form }) => (
          <Form onSubmit={handleSubmit} form={form}>
            <Field name="file">
              {(props) => {
                const {
                  input: { value, onChange, onClick, ...inputProps },
                } = props
                return (
                  <Form.Field>
                    <input
                      {...inputProps}
                      type="file"
                      accept=".csv"
                      disabled={isLookingUp}
                      style={{
                        width: 350,
                        border: 'none',
                        padding: '6px 2px',
                      }}
                      onChange={(event) => {
                        onChange(event.currentTarget.files?.[0])
                        form.submit()
                      }}
                      onClick={(event) => (event.currentTarget.value = '')}
                    />
                  </Form.Field>
                )
              }}
            </Field>
            <div style={{ marginTop: 5, marginBottom: 15 }}>
              <small>
                Requirements: (1) No header; (2) Column order: store name, name, alias, product
                type; (3) Available product types (case-insensitive); (4) [optional]
                pre-selected/highlighted product ID
                {Object.keys(PRODUCT_TYPE_ID_BY_KEY)
                  .filter((key) => key.length > 0)
                  .join(', ')}
                .
              </small>
            </div>
            <LookupOptionsFormGroup
              disabled={isLookingUp}
              style={{ marginLeft: 0 }}
              isBatchLookup={true}
            >
              <div className="field" style={{ paddingTop: '9px' }}>
                <Checkbox
                  label="Enable DeepMatcher"
                  checked={enableDeepMatcher}
                  disabled={isLookingUp}
                  onChange={(e, { checked }) => {
                    if (typeof checked !== 'undefined') {
                      dispatch(setEnableDeepMatcher({ enableDeepMatcher: checked }))
                      saveLocal('enableDeepMatcher', checked ? 'true' : 'false')
                    }
                  }}
                />
              </div>
            </LookupOptionsFormGroup>
          </Form>
        )}
      />
      <Transition
        visible={isLookingUp || batchProgressError.length > 0}
        animation="fade"
        duration={200}
      >
        <Divider />
      </Transition>
      {batchProgressError.length > 0 && (
        <Message color="red">Some errors were encountered. Continuing anyways..</Message>
      )}

      <Transition visible={isLookingUp} animation="fade" duration={200}>
        <div>
          Processing:
          <div
            style={{ paddingLeft: 4, minWidth: 35, display: 'inline-block' }}
          >{` ${currentProgress}`}</div>
          {` / ${totalProgress}`}
        </div>
      </Transition>
    </Segment>
  )
}

export default BatchFileUpload
